import { Base } from "../../framework/base";
import { allPartnersCompanyGroupCode } from "../consts";
import { GroupType, Permission, UserGroupType } from "../enums";
import { FolderNodePermission, FolderNodeSavePermission, IFolderNodePermission } from "./folderNodePermission";
import { FolderNodeSave, IFolderNodeSave } from "./folderNodeSave";
import { IFolderPermissionGroup } from "./folderPermissionGroup";

interface IPermissionCompanyGroupPermission {
    permission: Permission;
    companyGroupPermission: Permission;
    isExplicitPermission: boolean;
}

interface IGetChildPermissionsResult {
    permissions: { [id: string]: IPermissionCompanyGroupPermission; };
    maxPermission: number;
}

export interface IFolderNode {
    maxPermissionLevel: number;
    id: string;
    name: string;
    image: string;
    explicitPermissions: FolderNodePermission[];
    children: FolderNode[];
    parent: FolderNode;
    permission: IPermissionCompanyGroupPermission;
    expanded: boolean;

    hasChildren(): boolean;
    hasSiblings(): boolean;
    getIsRoot(): boolean;
    getAncesterIsCollapsed(): boolean;
    getFolderNodeById(id: string): IFolderNode;
    getSiblings(): IFolderNode[];
    hasExplicitPermission(companyGroupCode: string, userGroupCode: string): boolean;
    getNodePermissions(companyGroupCode: string, userGroupCode: string, list: { [id: string]: IFolderNodePermission; }): { [id: string]: IFolderNodePermission; };
    setGroupPermission(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup, permission: Permission, clearSubFolders: boolean);
    getEffectiveNonExplicitPermission(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup): IPermissionCompanyGroupPermission;
    setEffectivePermissions(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup, preCalculatedPermission: IPermissionCompanyGroupPermission);
    getJsonFolderNodeSaves(list: IFolderNodeSave[]): IFolderNodeSave[];
}

export class FolderNode implements IFolderNode {
    maxPermissionLevel: number;
    id: string;
    name: string;
    image: string;
    explicitPermissions: FolderNodePermission[];
    children: FolderNode[];
    parent: FolderNode;
    permission: IPermissionCompanyGroupPermission;
    expanded: boolean;

    constructor();
    constructor(obj: IFolderNode);
    constructor(obj?: any) {
        this.maxPermissionLevel = obj && obj.maxPermissionLevel || 0;
        this.id = obj && obj.id || "";
        this.name = obj && obj.name || "";
        this.image = obj && obj.image || "";
        this.explicitPermissions = null;
        if (!Base.isNullOrUndefined(obj) && !Base.isNullOrUndefined(obj.explicitPermissions)) {
            this.explicitPermissions = [];
            for (const explicitPermission of obj.explicitPermissions) {
                this.explicitPermissions.push(new FolderNodePermission(explicitPermission));
            }
        }
        this.children = [];
        this.parent = null;
        if (!Base.isNullOrUndefined(obj) && !Base.isNullOrUndefined(obj.children)) {
            for (const tempChild of obj.children) {
                const child = new FolderNode(tempChild);
                child.parent = this;
                this.children.push(child);
            }
        }
        this.permission = obj && obj.permission || { permission: Permission.None, companyGroupPermission: Permission.None };
        this.expanded = obj && obj.expanded || false;
    }

    getIsRoot(): boolean {
        return !this.parent;
    }

    getAncesterIsCollapsed(): boolean {
        if (!this.parent) return false;
        if (!this.parent.expanded) return true;
        return this.parent.getAncesterIsCollapsed();
    }

    getSiblings(): FolderNode[] {
        if (this.getIsRoot()) return [];
        return this.parent.children.filter(i => i.id !== this.id);
    }

    hasChildren(): boolean {
        return this.children.length > 0;
    }

    hasSiblings(): boolean {
        if (this.getIsRoot()) return false;
        return this.parent.children.length > 1;
    }

    getFolderNodeById(id: string): FolderNode {
        if (Base.isNullOrEmpty(id)) return null;
        if (this.id.equalIgnoreCase(id)) return this;
        for (let i = 0; i < this.children.length; i++) {
            const childResult = this.children[i].getFolderNodeById(id);
            if (childResult != null) return childResult;
        }
        return null;
    }

    removeGroupPermission(groupType: number, groupCode: string) {
        if (this.explicitPermissions === null || this.explicitPermissions.length === 0) return;
        const index = this.explicitPermissions.findIndex(i => i.type === groupType && i.code.equalIgnoreCase(groupCode));
        if (index >= 0) {
            this.explicitPermissions.splice(index, 1);
        }
    }

    removeGroupPermissions(groupType: number, groupCode: string) {
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
            const index = this.explicitPermissions.findIndex(i => i.type === groupType && i.code.equalIgnoreCase(groupCode));
            if (index >= 0) {
                this.explicitPermissions.splice(index, 1);
            }
        }
        if (this.children.length < 1) return;
        for (const child of this.children) {
            child.removeGroupPermissions(groupType, groupCode);
        }
    }

    setGroupPermission(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup, permission: Permission, clearSubFolders: boolean) {
        permission = Math.min(permission, Permission.Edit);
        //UserGroup / CompanyGroup selection
        let groupType: GroupType, groupSubType: UserGroupType, groupCode: string;
        if (!Base.isNullOrUndefined(userGroup)) {
            groupType = GroupType.UserGroup;
            groupCode = userGroup.code;
            groupSubType = userGroup.type;
        } else {
            groupType = GroupType.CompanyGroup;
            groupCode = companyGroup.code;
            groupSubType = companyGroup.type;
        }
        //Clear subfolder permissions
        if (clearSubFolders && this.hasChildren()) {
            for (let i = 0; i < this.children.length; i++) {
                this.children[i].removeGroupPermissions(groupType, groupCode);
            }
        }
        //Remove if equal to nonexplicit permission
        //if (doNotSetIfEqualsToNonExplicitPermission) {
        //    var nonExplicitPermission = this.getEffectiveNonExplicitPermission(companyGroup, userGroup);
        //    if (Math.min(nonExplicitPermission.permission, Permission.levelWrite) === permission) {
        //        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
        //            var index = this.explicitPermissions.findIndex(j => j.type === groupType && j.code.ciEqual(groupCode));
        //            if (index >= 0) {
        //                this.explicitPermissions.splice(index, 1);
        //            }
        //        }
        //        return;
        //    }
        //}
        //Add or Modify permission
        let currentPermission: FolderNodePermission = null;
        if (this.explicitPermissions === null) {
            this.explicitPermissions = [];
        } else {
            currentPermission = this.explicitPermissions.find(j => j.type === groupType && j.code.equalIgnoreCase(groupCode));
        }
        if (currentPermission === null || currentPermission === undefined) {
            currentPermission = new FolderNodePermission();
            currentPermission.type = groupType;
            currentPermission.groupType = groupSubType;
            currentPermission.code = groupCode;
            this.explicitPermissions.push(currentPermission);
        }
        currentPermission.level = permission;
    }

    setGroupPermissions(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup, permissions: { [id: string]: IFolderNodePermission; }) {
        //Remove old permission
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
            const index = !Base.isNullOrUndefined(userGroup)
                ? this.explicitPermissions.findIndex(i => i.code.equalIgnoreCase(userGroup.code) && i.type === GroupType.UserGroup)
                : this.explicitPermissions.findIndex(i => i.code.equalIgnoreCase(companyGroup.code) && i.type === GroupType.CompanyGroup);
            if (index >= 0) {
                this.explicitPermissions.splice(index, 1);
            }
        }
        //Add new permission if exist
        if (Object.prototype.hasOwnProperty.call(permissions, this.id)) {
            this.setGroupPermission(companyGroup, userGroup, permissions[this.id].level, false);
        }
        if (this.children.length < 1) return;
        for (let j = 0; j < this.children.length; j++) {
            this.children[j].setGroupPermissions(companyGroup, userGroup, permissions);
        }
    }

    getPermissionsBase(explicitPermissions: FolderNodePermission[]): FolderNodePermission[] {
        if (this.getIsRoot()) {
            return explicitPermissions;
        }
        let result = this.parent.getPermissions();
        if (explicitPermissions != null) {
            result = result.slice(0);
            let i: number;
            //'All' permissions replaces all same type inherited permissions
            const allPermissions = explicitPermissions.filter(k => Base.isNullOrEmpty(k.code));
            for (i = 0; i < allPermissions.length; i++) {
                const allPermission = allPermissions[i];
                result = result.filter(k => k.type !== allPermission.type);
            }
            //Normal permissions replaces only same type and code inherited permission
            for (i = 0; i < explicitPermissions.length; i++) {
                const explicitPermission = explicitPermissions[i];
                result = result.filter(k => !(k.type === explicitPermission.type && k.code.equalIgnoreCase(explicitPermission.code)));
                result.push(explicitPermission);
            }
        }
        return result;
    }

    getPermissions(): FolderNodePermission[] {
        return this.getPermissionsBase(this.explicitPermissions);
    }

    getEffectivePermissionBase(permissions: FolderNodePermission[], companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup): IPermissionCompanyGroupPermission {
        const result: IPermissionCompanyGroupPermission = {
            permission: this.maxPermissionLevel,
            companyGroupPermission: this.maxPermissionLevel,
            isExplicitPermission: false
        };
        let permission: FolderNodePermission;
        let i: number, j: number;
        //#1 Company group permissions are relevant only for users that do not belong to resource owning company
        //- Company of user that does not belong to resource owning company must always belong to normal company group in order to access folders
        //- Highest permissions considering all groups that user belongs to
        //- 'All group' permissions are used only if normal group permission does not exist in the same level
        if (!Base.isNullOrUndefined(companyGroup)) {
            let companyGroupPermissionsFound = false;
            let companyGroupPermission = Permission.None;
            let allCompanyGroupPermission: FolderNodePermission = null;
            let allPartnersCompanyGroupPermission: FolderNodePermission = null;
            let oneCompanyGroupPermission: FolderNodePermission = null;
            const relevantCompanyGroupPermissions = permissions.filter(k => k.type === GroupType.CompanyGroup);
            const companyGroups = companyGroup.groupCodes.slice(0);
            companyGroups.push(companyGroup.code);
            for (i = 0; i < relevantCompanyGroupPermissions.length; i++) {
                permission = relevantCompanyGroupPermissions[i];
                if (Base.isNullOrEmpty(permission.code)) { //'All companies' group
                    allCompanyGroupPermission = permission;
                } else if (permission.code.equalIgnoreCase(allPartnersCompanyGroupCode)) { //'All partners' group
                    allPartnersCompanyGroupPermission = permission;
                } else {
                    for (j = 0; j < companyGroups.length; j++) {
                        if (!permission.code.equalIgnoreCase(companyGroups[j])) continue;
                        if (permission.groupType === UserGroupType.Fixed) { //One company company group
                            oneCompanyGroupPermission = permission;
                        } else { //Normal company groups
                            companyGroupPermission = Math.max(companyGroupPermission, permission.level);
                            companyGroupPermissionsFound = true;
                        }
                        break;
                    }
                }
            }
            if (oneCompanyGroupPermission != null) {
                result.permission = Math.min(result.permission, oneCompanyGroupPermission.level);
            } else if (companyGroupPermissionsFound) {
                result.permission = Math.min(result.permission, companyGroupPermission);
            } else if (allPartnersCompanyGroupPermission != null) {
                result.permission = Math.min(result.permission, allPartnersCompanyGroupPermission.level);
            } else if (allCompanyGroupPermission != null) {
                result.permission = Math.min(result.permission, allCompanyGroupPermission.level);
            }
        }
        result.companyGroupPermission = result.permission;
        if (result.permission === Permission.None) return result;
        //#2 User group permissions are relevant for all users
        //- Highest permissions considering all groups that user belongs to
        //- 'All group' permissions are used only if normal group permission does not exist
        if (!Base.isNullOrUndefined(userGroup)) {
            let userGroupPermissionsFound = false;
            let userGroupPermission = Permission.None;
            let allUserGroupPermission: FolderNodePermission = null;
            let oneUserGroupPermission: FolderNodePermission = null;
            const relevantUserGroupPermissions = permissions.filter(k => k.type === GroupType.UserGroup);
            const userGroups = userGroup.groupCodes.slice(0);
            userGroups.push(userGroup.code);
            for (i = 0; i < relevantUserGroupPermissions.length; i++) {
                permission = relevantUserGroupPermissions[i];
                if (Base.isNullOrEmpty(permission.code)) { //'All users' group
                    allUserGroupPermission = permission;
                } else {
                    for (j = 0; j < userGroups.length; j++) {
                        if (!permission.code.equalIgnoreCase(userGroups[j])) continue;
                        if (permission.groupType === UserGroupType.Fixed) { //One user user group
                            oneUserGroupPermission = permission;
                        } else { //Normal user groups
                            userGroupPermission = Math.max(userGroupPermission, permission.level);
                            userGroupPermissionsFound = true;
                        }
                        break;
                    }
                }
            }
            if (oneUserGroupPermission != null) {
                result.permission = Math.min(result.permission, oneUserGroupPermission.level);
            } else if (userGroupPermissionsFound) {
                result.permission = Math.min(result.permission, userGroupPermission);
            } else if (allUserGroupPermission != null) {
                result.permission = Math.min(result.permission, allUserGroupPermission.level);
            }
        }
        return result;
    }

    getEffectivePermission(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup): IPermissionCompanyGroupPermission {
        const result = this.getEffectivePermissionBase(this.getPermissions(), companyGroup, userGroup);
        result.isExplicitPermission = this.getExplicitPermission(!Base.isNullOrUndefined(companyGroup) ? companyGroup.code : null, !Base.isNullOrUndefined(userGroup) ? userGroup.code : null) > -1;
        return result;
    }

    getEffectiveNonExplicitPermission(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup): IPermissionCompanyGroupPermission {
        const explicitPermissions = this.explicitPermissions != null ? this.explicitPermissions.slice(0) : null;
        if (!Base.isNullOrUndefined(explicitPermissions)) {
            const index = !Base.isNullOrUndefined(userGroup)
                ? explicitPermissions.findIndex(i => i.code.equalIgnoreCase(userGroup.code) && i.type === GroupType.UserGroup)
                : explicitPermissions.findIndex(i => i.code.equalIgnoreCase(companyGroup.code) && i.type === GroupType.CompanyGroup);
            if (index >= 0) {
                explicitPermissions.splice(index, 1);
            }
        }
        const result = this.getEffectivePermissionBase(this.getPermissionsBase(explicitPermissions), companyGroup, userGroup);
        result.isExplicitPermission = false;
        return result;
    }

    getChildPermissions(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup): IGetChildPermissionsResult {
        const result: IGetChildPermissionsResult = { permissions: {}, maxPermission: Permission.None };
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            const permission = child.getEffectivePermission(companyGroup, userGroup);
            result.permissions[child.id] = permission;
            result.maxPermission = Math.max(result.maxPermission, permission.permission);
        }
        return result;
    }

    getExplicitFolderNodePermission(companyGroupCode: string, userGroupCode: string): FolderNodePermission {
        let result: FolderNodePermission = null;
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
            result = !Base.isNullOrUndefined(userGroupCode)
                ? this.explicitPermissions.find(i => i.code.equalIgnoreCase(userGroupCode) && i.type === GroupType.UserGroup)
                : this.explicitPermissions.find(i => i.code.equalIgnoreCase(companyGroupCode) && i.type === GroupType.CompanyGroup);
        }
        return result;
    }

    getExplicitPermission(companyGroupCode: string, userGroupCode: string): number {
        const permission = this.getExplicitFolderNodePermission(companyGroupCode, userGroupCode);
        return !Base.isNullOrUndefined(permission) ? permission.level : -1;
    }

    hasExplicitPermission(companyGroupCode: string, userGroupCode: string): boolean {
        return this.getExplicitPermission(companyGroupCode, userGroupCode) > -1;
    }

    // getPermissionHtmlContent(isExplicitPermission: boolean, accessRightNoneTitle: string, accessRightReadTitle: string, accessRightWriteTitle: string): string {
    //     return (isExplicitPermission ? "<span class='icon tool'></span>" : "") +
    //         (this.permission.permission >= Permission.Edit
    //             ? accessRightWriteTitle
    //             : (this.permission.permission === Permission.Read ? accessRightReadTitle : accessRightNoneTitle));
    // }

    // getFolderPermissionHtml(): string {
    //     return "<span class='ol p" + Math.min(this.permission.permission, Permission.Edit) + "'></span>";
    // }

    // getAsHtml(accessRightNoneTitle: string, accessRightReadTitle: string, accessRightWriteTitle: string, isFirstNode: boolean, selectedId: string = null) {
    //     const hasChildren = this.children.length > 0;
    //     const hasVisibleChildren = hasChildren;
    //     const isRoot = this.getIsRoot();
    //     if (Base.isNullOrEmpty(selectedId) && isRoot) {
    //         selectedId = this.id;
    //     }
    //     const liClass = ((!Base.isNullOrEmpty(selectedId) && selectedId === this.id ? "selected" : "") + (!hasChildren ? " noChilds" : "") + (isRoot ? " root" : "")).trim();
    //     let result = "<ul>";
    //     result = result + "<li data-id='" + this.id + "'" + (!Base.isNullOrEmpty(liClass) ? " class='" + liClass + "'" : "") + ">";
    //     if (!isRoot && !isFirstNode) {
    //         result = result + "<span class='node" + (!hasVisibleChildren ? " empty" : "") + "'></span>";
    //     }
    //     const imageExists = !Base.isNullOrEmpty(this.image);
    //     const folderPermissionHtml = this.getFolderPermissionHtml();
    //     result = result + "<span class='icon folder" + (imageExists ? " hidden" : "") + "'></span>";
    //     result = result + "<img alt='folder' class='icon" + (!imageExists ? " hidden" : "") + "' src='" + (imageExists ? "data:image/png;base64," + this.image : "") + "'/>";
    //     result = result + "<div class='fol'>" + folderPermissionHtml + "</div>";
    //     const permissionHtml = "<span class='permission" + (this.permission.isExplicitPermission ? "" : " inherited") + "'>" + this.getPermissionHtmlContent(this.permission.isExplicitPermission, accessRightNoneTitle, accessRightReadTitle, accessRightWriteTitle) + "</span>";
    //     result = result + "<span class='name wp'>" + this.name + "</span>";
    //     result = result + permissionHtml;
    //     //console.log(permissionHtml);
    //     result = result + "</li>";
    //     if (hasVisibleChildren) {
    //         for (let i = 0; i < this.children.length; i++) {
    //             const child = this.children[i];
    //             const childTreeHtml = child.getAsHtml(accessRightNoneTitle, accessRightReadTitle, accessRightWriteTitle, false, selectedId);
    //             if (!Base.isNullOrEmpty(childTreeHtml)) {
    //                 result = result + childTreeHtml;
    //             }
    //         }
    //     }
    //     result = result + "</ul>";
    //     return result;
    // }

    setEffectivePermissions(companyGroup: IFolderPermissionGroup, userGroup: IFolderPermissionGroup, preCalculatedPermission: IPermissionCompanyGroupPermission = null) {
        const permission = preCalculatedPermission === null
            ? this.getEffectivePermission(companyGroup, userGroup)
            : preCalculatedPermission;
        this.permission = permission;
        //console.log(this.name + ": " + permission.permission);
        const permissionsOfChildren = this.getChildPermissions(companyGroup, userGroup);
        for (let i = 0; i < this.children.length; i++) {
            const child = this.children[i];
            child.setEffectivePermissions(companyGroup, userGroup, permissionsOfChildren.permissions[child.id]);
        }
    }

    getAsList(list: FolderNode[]): FolderNode[] {
        list.push(this);
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getAsList(list);
        }
        return list;
    }

    getJsonFolderNodeSaves(list: IFolderNodeSave[]): IFolderNodeSave[] {
        let permissions: FolderNodePermission[] = [];
        if (this.explicitPermissions !== null && this.explicitPermissions.length > 0) {
            permissions = this.explicitPermissions.slice(0);
        }
        let saveFolderNode = list.find(j => j.id === this.id);
        if (Base.isNullOrUndefined(saveFolderNode)) {
            saveFolderNode = new FolderNodeSave();
            saveFolderNode.id = this.id;
            list.push(saveFolderNode);
        }
        for (const permission of permissions) {
            if (saveFolderNode.permissions.findIndex(j => j.type === permission.type && j.code.equalIgnoreCase(permission.code)) >= 0) continue;
            const savePermission = new FolderNodeSavePermission();
            savePermission.type = permission.type;
            savePermission.code = permission.code;
            savePermission.level = permission.level;
            saveFolderNode.permissions.push(savePermission);
        }
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getJsonFolderNodeSaves(list);
        }
        return list;
    }

    getNodePermissions(companyGroupCode: string, userGroupCode: string, list: { [id: string]: IFolderNodePermission; }): { [id: string]: IFolderNodePermission; } {
        const permission = this.getExplicitFolderNodePermission(companyGroupCode, userGroupCode);
        if (!Base.isNullOrUndefined(permission)) {
            list[this.id] = new FolderNodePermission(permission);
        }
        if (this.children.length < 1) return list;
        for (const child of this.children) {
            child.getNodePermissions(companyGroupCode, userGroupCode, list);
        }
        return list;
    }

    // updatePermissionsToTree(htmlTree: JQuery, accessRightNoneTitle: string, accessRightReadTitle: string, accessRightWriteTitle: string) {
    //     const liElements = htmlTree.find("li");
    //     const folderNodes = this.getAsList([]);
    //     liElements.each(function () {
    //         const li = $(this);
    //         const id = li.attr("data-id");
    //         const folderNode = folderNodes.find(i => i.id === id);
    //         if (!isNullOrUndefined(folderNode)) {
    //             const folderPermissionHtml = folderNode.getFolderPermissionHtml();
    //             const folderElement = li.find(".fol");
    //             if (folderElement.length) {
    //                 folderElement.html(folderPermissionHtml);
    //             }
    //             const permissionElement = li.find(".permission");
    //             if (!folderNode.permission.isExplicitPermission) {
    //                 permissionElement.addClass("inherited");
    //             } else {
    //                 permissionElement.removeClass("inherited");
    //             }
    //             permissionElement.html(folderNode.getPermissionHtmlContent(folderNode.permission.isExplicitPermission, accessRightNoneTitle, accessRightReadTitle, accessRightWriteTitle));
    //         }
    //     });
    // }
}